import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { SectionFooter, settingsButtonSx, settingsText } from "../settings-section";
import { clientConfigService } from "../../utils/services/clientConfigService";
import { metricsService } from "../../utils/services/metricsService";
import {
  SECTION_DAILY,
  SECTION_HISTORY,
  SECTION_KPI,
  SECTION_TREND,
  normalizeDashboard,
} from "../metrics/salesDashboardConfig";

export const SALES_DASHBOARD_KEY = "dashboard.sales_over_time";
const MIN_KPIS = 2;
const MAX_KPIS = 8;

const SECTIONS = [
  { id: SECTION_KPI, label: "KPI summary", help: "Headline metric tiles" },
  {
    id: SECTION_TREND,
    label: "Trend chart",
    help: "Gross sales and orders by month or fiscal period",
  },
  {
    id: SECTION_HISTORY,
    label: "History tables",
    help: "Yearly and month / fiscal period tables",
  },
  {
    id: SECTION_DAILY,
    label: "Daily detail",
    help: "Daily charts, daily table and SKU drill-down",
  },
];
const SOLO_ALLOWED = [SECTION_KPI, SECTION_HISTORY];

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/** Editor draft from the resolved dashboard the server returned. */
const draftFromDashboard = (dashboard) => {
  const resolved = normalizeDashboard(dashboard);
  return {
    sections: Object.fromEntries(
      SECTIONS.map(({ id }) => [id, resolved.sections.includes(id)]),
    ),
    metrics: resolved.kpi_metrics.length
      ? resolved.kpi_metrics
      : normalizeDashboard(null).kpi_metrics,
  };
};

export const draftProblem = (draft) => {
  const enabled = SECTIONS.filter(({ id }) => draft.sections[id]).map(
    ({ id }) => id,
  );
  if (!enabled.length) return "Enable at least one section.";
  if (enabled.length === 1 && !SOLO_ALLOWED.includes(enabled[0])) {
    return "A dashboard with one section must show the KPI summary or the history tables.";
  }
  if (
    draft.sections[SECTION_KPI] &&
    (draft.metrics.length < MIN_KPIS || draft.metrics.length > MAX_KPIS)
  ) {
    return `Select ${MIN_KPIS}–${MAX_KPIS} KPI metrics.`;
  }
  return null;
};

export const draftToValue = (draft) => ({
  schema_version: 1,
  sections: {
    [SECTION_KPI]: draft.sections[SECTION_KPI]
      ? { enabled: true, metrics: draft.metrics }
      : { enabled: false },
    [SECTION_TREND]: { enabled: draft.sections[SECTION_TREND] },
    [SECTION_HISTORY]: { enabled: draft.sections[SECTION_HISTORY] },
    [SECTION_DAILY]: { enabled: draft.sections[SECTION_DAILY] },
  },
});

/**
 * Sales over Time dashboard settings for one client
 * (`dashboard.sales_over_time` in clientConfig), shown in the Dashboard
 * settings dialog. The server validates every save and only lets a session
 * edit its own client (internal admins: any client).
 */
const SalesDashboardSetting = ({ clientId, onSaved, onCancel }) => {
  const [row, setRow] = useState(null);
  const [options, setOptions] = useState([]);
  const [resolved, setResolved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [rows, listed] = await Promise.all([
        clientConfigService.fetch(clientId, 1, 0, SALES_DASHBOARD_KEY),
        metricsService.fetchFiscalPeriods({ clientId }),
      ]);
      setRow(
        (rows?.data || []).find((r) => r.key === SALES_DASHBOARD_KEY) || null,
      );
      setOptions(listed?.available_kpi_metrics || []);
      setResolved(listed?.dashboard || null);
      setDraft(draftFromDashboard(listed?.dashboard || null));
    } catch (error) {
      setLoadError(errorMessage(error, "Couldn't load the dashboard settings."));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSection = (id) =>
    setDraft((prev) => ({
      ...prev,
      sections: { ...prev.sections, [id]: !prev.sections[id] },
    }));

  // Order = selection order; a new pick goes last.
  const toggleMetric = (id) =>
    setDraft((prev) => ({
      ...prev,
      metrics: prev.metrics.includes(id)
        ? prev.metrics.filter((m) => m !== id)
        : [...prev.metrics, id],
    }));

  const save = async () => {
    setSaving(true);
    try {
      const value = draftToValue(draft);
      if (row?._id) {
        await clientConfigService.edit(row._id, {
          value,
          expected_updated_at: row.updatedAt,
        });
      } else {
        await clientConfigService.create({
          client_id: clientId,
          key: SALES_DASHBOARD_KEY,
          value,
        });
      }
      toast.success("Dashboard settings saved.");
      onSaved?.();
    } catch (error) {
      toast.error(errorMessage(error, "Couldn't save the dashboard settings."));
    } finally {
      setSaving(false);
    }
  };

  const restoreDefault = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Restore the standard Sales over Time dashboard?")) {
      return;
    }
    setSaving(true);
    try {
      await clientConfigService.delete(row._id);
      toast.success("Standard dashboard restored.");
      onSaved?.();
    } catch (error) {
      toast.error(errorMessage(error, "Couldn't restore the standard dashboard."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
        <CircularProgress size={14} />
        <Typography sx={settingsText.muted}>Loading…</Typography>
      </Box>
    );
  }
  if (loadError) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
        <Typography sx={settingsText.muted}>{loadError}</Typography>
        <Button size="small" onClick={load} sx={settingsButtonSx}>
          Retry
        </Button>
      </Box>
    );
  }

  const warnings = resolved?.warnings || [];
  const problem = draftProblem(draft);

  return (
    <Box data-testid="sales-dashboard-setting">
      <Typography sx={settingsText.muted} data-testid="dashboard-status">
        {row
          ? `Customized · last saved ${new Date(row.updatedAt).toLocaleString()}${
              row.value?.updated_by?.email ? ` by ${row.value.updated_by.email}` : ""
            }`
          : "Standard dashboard (no custom settings saved)."}
      </Typography>

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5, fontFamily: "poppins", fontSize: 12.5 }}>
          Some saved items are no longer available and are skipped:{" "}
          {warnings.map((w) => w.id || w.code).join(", ")}.
        </Alert>
      )}

      <Typography sx={{ ...settingsText.label, mt: 2.5, mb: 0.5 }}>Sections</Typography>
      <Box sx={{ display: "grid", mb: 2 }}>
        {SECTIONS.map(({ id, label, help }) => (
          <FormControlLabel
            key={id}
            sx={{ m: 0, py: 0.25, alignItems: "center" }}
            control={
              <Switch
                size="small"
                checked={draft.sections[id]}
                onChange={() => toggleSection(id)}
                inputProps={{ "aria-label": label }}
              />
            }
            label={
              <Box sx={{ ml: 1 }}>
                <Typography component="span" sx={{ ...settingsText.value, fontWeight: 500 }}>
                  {label}
                </Typography>
                <Typography component="span" sx={{ ...settingsText.muted, ml: 1 }}>
                  {help}
                </Typography>
              </Box>
            }
          />
        ))}
        <Typography sx={{ ...settingsText.muted, mt: 0.5 }}>
          Metric definitions and data availability notes always show. Section
          order is fixed.
        </Typography>
      </Box>

      {draft.sections[SECTION_KPI] && (
        <Box>
          <Typography sx={{ ...settingsText.label, mb: 0.5 }}>
            KPI tiles · {draft.metrics.length} selected
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            }}
          >
            {options.map((metric) => {
              const position = draft.metrics.indexOf(metric.id);
              return (
                <FormControlLabel
                  key={metric.id}
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={position >= 0}
                      onChange={() => toggleMetric(metric.id)}
                    />
                  }
                  label={
                    <Typography sx={settingsText.value}>
                      {position >= 0 ? `${position + 1}. ` : ""}
                      {metric.display_name}
                    </Typography>
                  }
                />
              );
            })}
          </Box>
          <Typography sx={{ ...settingsText.muted, mt: 0.5 }}>
            Tiles appear in the order you select them ({MIN_KPIS}–
            {Math.min(MAX_KPIS, options.length || MAX_KPIS)}).
          </Typography>
        </Box>
      )}

      {problem && (
        <Alert severity="info" sx={{ mt: 2, fontFamily: "poppins", fontSize: 12.5 }}>
          {problem}
        </Alert>
      )}

      <SectionFooter
        left={
          row && (
            <Button
              onClick={restoreDefault}
              disabled={saving}
              sx={{ ...settingsButtonSx, color: "var(--text-secondary)", px: 1 }}
            >
              Restore standard dashboard
            </Button>
          )
        }
      >
        <Button
          onClick={onCancel}
          disabled={saving}
          sx={{ ...settingsButtonSx, color: "var(--text-secondary)" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={Boolean(problem) || saving}
          onClick={save}
          sx={settingsButtonSx}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </SectionFooter>
    </Box>
  );
};

export default SalesDashboardSetting;
